
import { Component, Vue } from 'vue-property-decorator'
import { MediaService } from '@/services/index'
import { IPlatform, IAcct } from '@/types/media-types'
import AddPlatform from './components/addPlatform.vue'

// 消除导航栏的高度
const differScroll = 110

const mapStatusToTxt = {
    CONFIGURED_SUCCESS: '√',
    CONFIGURED_PARTIAL: '▓',
    CONFIGURED_FAILED: '╳',
    CONFIGURED_STOP_UPDATE: '！',
    CHECKED_NOT_CONFIGURED: '☉',
    CHECKED_NOT_EXIST: '□',
    NOT_CHECKED: '?'
}

enum bindingStatus {
    CONFIGURED_SUCCESS = 'CONFIGURED_SUCCESS',
    CHECKED_NOT_CONFIGURED = 'CHECKED_NOT_CONFIGURED',
    CHECKED_NOT_EXIST = 'CHECKED_NOT_EXIST',
    NOT_CHECKED = 'NOT_CHECKED',
    CONFIGURED_FAILED = 'CONFIGURED_FAILED',
    CONFIGURED_STOP_UPDATE = 'CONFIGURED_STOP_UPDATE'
}

@Component({
    name: 'MediaPlatforms',
    components: {
        AddPlatform
    }
})
export default class extends Vue {
    private pagePbHeight: string = '0px'
    private curMenu: number = 0
    private mediaId: number = 0
    private media: any = {}
    private mediaType: any = ''
    private platforms: any = []
    private grouped: any = []
    private expandedGroups: boolean[] = []
    private acctStatusMap = {
        related: 1,
        unrelated: 2
    }
    private bindingOptions = [
        { value: bindingStatus.CHECKED_NOT_CONFIGURED, label: '☉（已检查有，未配置）' },
        { value: bindingStatus.CHECKED_NOT_EXIST, label: '□（已检查，不存在）' },
        { value: bindingStatus.NOT_CHECKED, label: '?（未检查，不知）' }
    ]
    private memoOptions = [
        { value: bindingStatus.CONFIGURED_STOP_UPDATE, label: '！（已配置，账号已停更）' },
        { value: bindingStatus.CONFIGURED_FAILED, label: '╳（已配置，爬取失败）' }
    ]
    private platformBindingStatusDialogVisible = false
    private accountMemoDialogVisible: boolean = false
    private platformBindingStatus: string = bindingStatus.NOT_CHECKED
    private platformAccountMemo: string = ''
    private curPlatform: IPlatform | null = null
    private currentAccount: IAcct | undefined = undefined 

    get isSMedia() {
        return +this.mediaId > 10000000
    }

    private async mounted() {
        // 注册滚动监听器
        window.addEventListener('scroll', this.onScroll, false)
        this.initData()
        await Promise.all([
            this.getMediaDetail(),
            this.getPlatforms()
        ])
    }

    private destroyed() {
        // 必须移除监听器，不然当该vue组件被销毁了，监听器还在就会出错
        window.removeEventListener('scroll', this.onScroll)
    }

    private initData() {
        this.mediaId = parseInt(this.$route.params.id)
        this.pagePbHeight = (document.body.clientHeight - differScroll - 128) + 'px'
    }

    // 获取媒体详情
    private async getMediaDetail() {
        const result = await MediaService.getDetail({ id: this.mediaId })
        if (result.status === 200) {
            this.media = result.data
        }
    }

    // 获取平台列表
    private async getPlatforms() {
        const params = {
            media_type: this.isSMedia ? 'self_media' : 'media',
            media_id: this.mediaId
        }
        const result = await MediaService.getPlatforms(params)
        if (result.status === 200) {
            const { platforms, groups = [] } = result.data
            let scrollIndex = 1
            const filteredPlatforms = this.filterPlatforms(platforms).map((platform: any) => ({
                type: 'platform',
                data: platform,
                scrollIndex: scrollIndex++
            }))

            const groupedPlatforms = groups.map((group: any) => ({
                type: 'group',
                data: {
                    ...group,
                    platforms: this.filterPlatforms(group.platforms).map((platform: any) => ({
                        ...platform,
                        scrollIndex: scrollIndex++
                    }))
                }
            }))

            this.grouped = [...filteredPlatforms, ...groupedPlatforms]
            this.expandedGroups = this.grouped.map(() => true)
        }
    }


    // 切换父目录的展开/折叠状态
    private toggleGroup(groupIndex: number) {
        this.$set(this.expandedGroups, groupIndex, !this.expandedGroups[groupIndex])
    }

    private filterPlatforms(platforms: IPlatform[]) {
        const result = platforms.map((platform: any) => {
            let relatedAcctCount = 0
            platform.related_accts = []
            platform.unrelated_accts = []
            platform.accounts.map((acct: IAcct) => {
                if (acct.status === this.acctStatusMap.related) {
                    relatedAcctCount += 1
                    platform.related_accts.push(acct)
                } else {
                    platform.unrelated_accts.push(acct)
                }
            })
            platform.related_acct_count = relatedAcctCount
            return platform
        })
        return result
    }

    private onRemoveAcct(platform: IPlatform, acct: IAcct) {
        const title = '是否取消「' + platform.name + '」与【' + acct.name + '】的关联'
        const desc = '取消后，对应的爬虫程序将马上停止运行'
        this.isConfirmed(title, desc).then(() => {
            this.updateAcct(acct, this.acctStatusMap.unrelated)
        }).catch(() => {})
    }

    private onUpdateAcct(platform: IPlatform, acct: IAcct) {
        const title = '是否将「' + platform.name + '」与【' + acct.name + '】关联'
        const desc = '关联账号后，对应的爬虫程序立马启动'
        this.isConfirmed(title, desc).then(() => {
            this.updateAcct(acct, this.acctStatusMap.related)
        }).catch(() => {})
    }

    private onAddPlatform(platform: IPlatform, media: any) {
        (this.$refs.addPlatform as any).show(this.mediaId, platform, media, () => {
            this.getPlatforms()
        })
    }

    private async updateAcct(acct: IAcct, status: number) {
        const params = { account_id: acct.id, status: status, media_type: this.isSMedia ? 'self_media' : 'media' }
        const result = await MediaService.updateAcct(params)
        if (result.status === 200) {
            await this.getPlatforms()
            this.$message({ type: 'success', message: '操作成功' })
        }
    }

    private isConfirmed(title: string, desc: string) {
        const h = this.$createElement
        return this.$msgbox({
            title: '确认',
            message: h('p', null as any, [
                h('div', null as any, title),
                h('div', { style: 'margin-top: 10px;' }, desc)
            ]),
            showCancelButton: true,
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            center: true
        })
    }

    private mapAccountStatus(account: IAcct) {
        const { collection_status, memo } = account

        if (memo) {
            return (mapStatusToTxt as any)[memo]
        }

        switch (collection_status) {
            case '正常':
                return mapStatusToTxt.CONFIGURED_SUCCESS
            case '异常':
                return mapStatusToTxt.CONFIGURED_FAILED
            default:
                return mapStatusToTxt.CONFIGURED_SUCCESS
        }   

    }

    private mapPlatformbBindingStatus(status: string) {
        switch (status) {
            case bindingStatus.CHECKED_NOT_CONFIGURED:
                return mapStatusToTxt.CHECKED_NOT_CONFIGURED
            case bindingStatus.CHECKED_NOT_EXIST:
                return mapStatusToTxt.CHECKED_NOT_EXIST
            default:
                return mapStatusToTxt.NOT_CHECKED
        }   

    }

    private handlePlatformBindingStatus(platform: IPlatform) {
        this.curPlatform = platform
        this.platformBindingStatusDialogVisible = true
        this.platformBindingStatus = platform.binding_status || bindingStatus.NOT_CHECKED
    }

    private async handlePlatformBindingStatusChange(value: string) {
        this.platformBindingStatus = value

        const { id, name } = this.curPlatform as IPlatform
        const params = {
            medium_id: this.mediaId,
            binding_status_id: id,
            binding_status_name: name,
            binding_status: this.platformBindingStatus
        }

        const result = await MediaService.updateMediaPlatformBindingStatus(params)
        if (result.status === 200) {
            (this.curPlatform as IPlatform).binding_status = this.platformBindingStatus
            this.$message.success('更新成功')
        } else {
            this.$message.error('更新失败')
        }
    }   

    private handleAccountMemoDialogClose() {
        this.accountMemoDialogVisible = false
    }

    private handleAccountMemoDialogOpen(account: IAcct) {
        if (account.collection_status === '正常') return
        
        this.currentAccount = account
        this.platformAccountMemo = account.memo ? account.memo : (account.collection_status === '正常' ? bindingStatus.CONFIGURED_SUCCESS : bindingStatus.CONFIGURED_FAILED)
        this.accountMemoDialogVisible = true 
    }

    private async handleAccountMemoUpdate() {
        const params = {
            mp_id: (this.currentAccount as IAcct).id,
            data: this.platformAccountMemo
        }
        const result = await MediaService.updateMpAccountMemo(params)
        if (result.status === 200) {
            (this.currentAccount as IAcct).memo = this.platformAccountMemo
            this.$message.success('更新成功')
            this.accountMemoDialogVisible = false
        } else {
            this.$message.error('更新失败')
        }
    }

    // 滚动监听器
    private onScroll() {
        // 获取所有锚点元素
        const navContents = document.querySelectorAll('.accts-info .acct-item')
        // 所有锚点元素的 offsetTop
        const offsetTopArr: any = []
        navContents.forEach((item: any) => {
            offsetTopArr.push(item.offsetTop)
        })
        // 获取当前文档流的 scrollTop
        const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
        // 定义当前点亮的导航下标
        let navIndex = 0
        for (let n = 0; n < offsetTopArr.length; n++) {
            // 如果 scrollTop 大于等于第n个元素的 offsetTop 则说明 n-1 的内容已经完全不可见
            // 那么此时导航索引就应该是n了
            if (scrollTop >= offsetTopArr[n] - differScroll) {
                navIndex = n
            }
        }
        this.curMenu = navIndex
    }

    // 跳转到指定索引的元素
    private scrollTo(index: number) {
        this.curMenu = index
        const targetElement = document.querySelector(`.accts-info .acct-item[data-scroll-index="${index}"]`)
        // 获取目标的 offsetTop
        // css选择器是从 1 开始计数，我们是从 0 开始，所以要 +1
        const targetOffsetTop = (targetElement as HTMLElement).offsetTop
        // 获取当前 offsetTop
        // default:: let scrollTop = document.documentElement.scrollTop || document.body.scrollTop
        const scrollEl: HTMLElement | null = document.querySelector('.app-container')
        let scrollTop = scrollEl!.scrollTop
        // 定义一次跳 50 个像素，数字越大跳得越快，但是会有掉帧得感觉，步子迈大了会扯到蛋
        const STEP = 50
        // 判断是往下滑还是往上滑
        if (scrollTop > targetOffsetTop) {
            // 往上滑
            smoothUp()
        } else {
            // 往下滑
            smoothDown()
        }
        // 定义往下滑函数
        function smoothDown() {
            // 如果当前 scrollTop 小于 targetOffsetTop 说明视口还没滑到指定位置
            if (scrollTop < targetOffsetTop) {
                // 如果和目标相差距离大于等于 STEP 就跳 STEP
                // 否则直接跳到目标点，目标是为了防止跳过了。
                if (targetOffsetTop - scrollTop >= STEP) {
                    scrollTop += STEP
                } else {
                    scrollTop = targetOffsetTop
                }
                // 
                // document.documentElement.scrollTop = scrollTop - differScroll
                // document.body.scrollTop = scrollTop - differScroll
                scrollEl!.scrollTop = scrollTop - differScroll
                // 关于 requestAnimationFrame 可以自己查一下，在这种场景下，相比 setInterval 性价比更高
                requestAnimationFrame(smoothDown)
            }
        }
        // 定义往上滑函数
        function smoothUp() {
            if (scrollTop > targetOffsetTop) {
                if (scrollTop - targetOffsetTop >= STEP) {
                    scrollTop -= STEP
                } else {
                    scrollTop = targetOffsetTop
                }
                // document.body.scrollTop = scrollTop - differScroll
                // document.documentElement.scrollTop = scrollTop - differScroll
                scrollEl!.scrollTop = scrollTop - differScroll
                requestAnimationFrame(smoothUp)
            }
        }
    }
}
